/**
 * DesktopViewer: connects to a remote VNC desktop via WebRTC DataChannel.
 *
 * Flow:
 * 1. Connect to MQTT broker for signaling
 * 2. Create WebRTC PeerConnection with DataChannel
 * 3. Send offer via MQTT, receive answer
 * 4. Once DataChannel opens, pass it to noVNC RFB
 */
import { SignalingClient, SignalingError } from "./signaling";
export class ViewerError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "ViewerError";
        this.code = code;
    }
}
let RFB = null;
async function loadRFB() {
    if (!RFB) {
        // @ts-expect-error noVNC doesn't ship proper TS types
        const mod = await import("@novnc/novnc/lib/rfb.js");
        RFB = mod.default ?? mod;
        patchOutAppleAuth(RFB);
    }
    return RFB;
}
/**
 * Monkey-patch noVNC's `_isSupportedSecurityType` so it pretends RFB
 * scheme 30 (Apple Authentication / ARD) is NOT supported, forcing
 * the negotiator to fall through to whatever the server lists next.
 *
 * Why: macOS Screen Sharing always advertises scheme 30 first
 * (verified raw RFB probe: order is `[30, 33, 36, 2, 35]`) regardless
 * of whether `ARD_AllLocalUsers` is on — the ARD plist key controls
 * **who** can authenticate via scheme 30, not whether it's offered.
 * noVNC's `_negotiateSecurity` picks the first server scheme that
 * `_isSupportedSecurityType` recognizes, so untouched it always
 * picks 30 and then loops on `credentialsrequired` because the
 * dashboard's streamParams flow only carries `vncPassword` (the
 * legacy VNC viewer password), not a macOS user login.
 *
 * After the patch noVNC will skip 30 → skip 33 (unsupported) →
 * skip 36 (unsupported) → pick 2 (VNCAuth) → use `vncPassword` →
 * succeed. The override is process-local and safe: scheme 30 is
 * never the right choice for the streamParams.vncPassword pipeline.
 */
function patchOutAppleAuth(Rfb) {
    const proto = Rfb?.prototype;
    if (!proto || typeof proto._isSupportedSecurityType !== "function") {
        // noVNC internals changed shape — we'd rather log + run unpatched
        // than fail loudly; the pre-patch behaviour is exactly the
        // pre-existing bug, not a regression.
        // eslint-disable-next-line no-console
        console.warn("[desktop-viewer] noVNC `_isSupportedSecurityType` not found; " +
            "Apple Auth (RFB scheme 30) will not be filtered out.");
        return;
    }
    if (proto.__beeosPatched)
        return;
    const RFB_SCHEME_ARD = 30;
    const original = proto._isSupportedSecurityType;
    proto._isSupportedSecurityType = function patched(type) {
        if (type === RFB_SCHEME_ARD)
            return false;
        return original.call(this, type);
    };
    proto.__beeosPatched = true;
}
/**
 * Default lead time before `streamParams.expiresAt` at which the
 * pre-emptive refresh fires. 60 s is the same window used by the
 * mobile `@beeos/device-viewer` SDK and is comfortably larger than
 * the worst-case `/api/v1/instances/{id}/stream-url` round-trip we've
 * seen in production (~2 s p99).
 */
const DEFAULT_REFRESH_LEAD_MS = 60_000;
const REFRESH_RETRY_MS = 15_000;
const DISCONNECTED_GRACE_MS = 8_000;
const CONNECTION_TIMEOUT_MS = 45_000;
const MAX_RECOVERY_DELAY_MS = 30_000;
export class DesktopViewer {
    signaling = null;
    pc = null;
    dc = null;
    rfb = null;
    cachedRfb = null;
    options;
    _state = "disconnected";
    /** Serializes setRemoteDescription(answer): MQTT may deliver duplicate answers (QoS / double publish). */
    remoteAnswerQueue = Promise.resolve();
    /** setTimeout handle for the pre-emptive refresh. Cleared on disconnect / re-scheduled after each refresh. */
    refreshTimer = null;
    /**
     * Single-flight guard for `tryRefresh`. The pre-emptive timer and a
     * concurrent `auth_failed` error can race; we de-dupe by sharing
     * one in-flight promise. Cleared in finally so the next legitimate
     * trigger gets a fresh attempt.
     */
    refreshInflight = null;
    /**
     * Latched-on flag — set after a refresh attempt fails with
     * "no `refreshStreamParams` callback provided". Once we know the
     * caller can't refresh we stop nagging and just degrade to the
     * existing failed UI on the next auth error.
     */
    refreshUnavailable = false;
    stopped = false;
    wasConnected = false;
    generation = 0;
    recoveryAttempts = 0;
    recoveryTimer = null;
    disconnectedTimer = null;
    connectionTimer = null;
    retryInflight = null;
    initialAuthRefreshAttempted = false;
    constructor(options) {
        this.options = options;
    }
    get state() {
        return this._state;
    }
    async connect() {
        this.stopped = false;
        const generation = ++this.generation;
        this.setState("connecting");
        this.remoteAnswerQueue = Promise.resolve();
        // Cancel any leftover refresh timer from a previous connect()
        // cycle. scheduleRefresh() re-arms it after a successful connect.
        this.clearRefreshTimer();
        try {
            // Pre-load noVNC RFB module so onDataChannelOpen is synchronous.
            // Without this, the async import creates a window where the VNC server's
            // initial RFB version string arrives on the DataChannel before noVNC has
            // attached its onmessage handler, causing data loss and a stuck handshake.
            const Rfb = await loadRFB();
            if (!this.isCurrent(generation))
                return;
            this.cachedRfb = Rfb;
            // Fail before opening MQTT so a browser without WebRTC cannot leave
            // a live signaling client repeatedly overwriting the useful error.
            if (typeof RTCPeerConnection !== "function") {
                throw new ViewerError("webrtc_unavailable", "WebRTC is unavailable in this browser");
            }
            this.signaling = new SignalingClient({
                mqttUrl: this.options.streamParams.mqttUrl,
                mqttToken: this.options.streamParams.mqttToken,
                deviceTopic: this.options.streamParams.deviceTopic,
                onAnswer: (sdp) => { if (this.isCurrent(generation))
                    this.enqueueRemoteAnswer(sdp); },
                onIceCandidate: (init) => { if (this.isCurrent(generation))
                    void this.handleRemoteIceCandidate(init); },
                onError: (err) => {
                    if (!this.isCurrent(generation))
                        return;
                    if (this._state === "connecting" && !(err instanceof SignalingError && err.code === "auth_failed")) {
                        this.closeTransport();
                    }
                    this.handleError(err);
                },
            });
            await this.signaling.connect();
            if (!this.isCurrent(generation))
                return;
            const pc = new RTCPeerConnection({
                iceServers: this.options.streamParams.iceServers,
            });
            this.pc = pc;
            pc.onicecandidate = (event) => {
                if (!this.isCurrent(generation) || this.pc !== pc)
                    return;
                if (event.candidate) {
                    // MQTT can briefly disconnect during ICE gathering. A missed
                    // candidate causes ICE to fail and the full session to recover.
                    try {
                        this.signaling?.sendIceCandidate(event.candidate);
                    }
                    catch { /* reconnect below */ }
                }
            };
            pc.onconnectionstatechange = () => {
                if (!this.isCurrent(generation) || this.pc !== pc)
                    return;
                const state = pc.connectionState;
                if (state === "connected")
                    this.clearDisconnectedTimer();
                if (state === "disconnected" && !this.disconnectedTimer) {
                    this.disconnectedTimer = setTimeout(() => {
                        this.disconnectedTimer = null;
                        if (this.pc === pc && pc.connectionState === "disconnected")
                            this.transportLost(generation);
                    }, DISCONNECTED_GRACE_MS);
                }
                if (state === "failed" || state === "closed") {
                    this.transportLost(generation);
                }
            };
            this.dc = pc.createDataChannel("vnc", {
                ordered: true,
                protocol: "binary",
                negotiated: true,
                id: 0,
            });
            this.dc.binaryType = "arraybuffer";
            this.dc.onopen = () => { if (this.isCurrent(generation))
                this.onDataChannelOpen(generation); };
            const offer = await pc.createOffer();
            if (!this.isCurrent(generation))
                return;
            await pc.setLocalDescription(offer);
            if (!this.isCurrent(generation))
                return;
            this.signaling.sendOffer(offer.sdp);
            // Successful signaling.connect() means we have a healthy MQTT
            // session. Schedule the pre-emptive refresh now so the first
            // tryRefresh fires ~60s before this token expires (assuming
            // the caller wired up `refreshStreamParams`).
            this.scheduleRefresh();
            this.connectionTimer = setTimeout(() => {
                this.connectionTimer = null;
                if (this.isCurrent(generation) && this._state === "connecting")
                    this.transportLost(generation);
            }, CONNECTION_TIMEOUT_MS);
        }
        catch (err) {
            if (!this.isCurrent(generation))
                return;
            this.closeTransport();
            this.handleError(err instanceof Error ? err : new Error(String(err)));
        }
    }
    disconnect() {
        this.stopped = true;
        this.wasConnected = false;
        this.recoveryAttempts = 0;
        this.clearRecoveryTimer();
        this.closeTransport();
        this.setState("disconnected");
    }
    closeTransport() {
        ++this.generation;
        this.clearRefreshTimer();
        this.clearDisconnectedTimer();
        this.clearConnectionTimer();
        const rfb = this.rfb;
        this.rfb = null;
        const dc = this.dc;
        this.dc = null;
        const pc = this.pc;
        this.pc = null;
        const signaling = this.signaling;
        this.signaling = null;
        this.remoteAnswerQueue = Promise.resolve();
        // Each layer must be released even if the preceding one already failed.
        try {
            rfb?.disconnect();
        }
        catch { /* already closed */ }
        try {
            dc?.close();
        }
        catch { /* already closed */ }
        try {
            pc?.close();
        }
        catch { /* already closed */ }
        try {
            signaling?.disconnect();
        }
        catch { /* already closed */ }
    }
    /**
     * Public retry entry point. Called by the UI's "Retry" button. Goes
     * through `tryRefresh("user_retry")` so even a fresh user-initiated
     * retry picks up new credentials before re-attempting — that's the
     * fix for the "Bad User Name or Password" loop the original retry
     * button used to land in (it re-used the expired streamParams).
     *
     * If refresh is unavailable (no callback) we fall through to a
     * plain disconnect+connect cycle — preserves the legacy behaviour
     * for callers that haven't migrated yet.
     */
    async retry() {
        if (this.retryInflight)
            return this.retryInflight;
        this.initialAuthRefreshAttempted = false;
        this.clearRecoveryTimer();
        const task = (async () => {
            const fresh = await this.tryRefresh("user_retry");
            if (this.stopped)
                return;
            // Keep a live connection intact if the credential endpoint is
            // temporarily unavailable; the recovery timer will try again.
            if (this.options.refreshStreamParams && !fresh) {
                if (this.wasConnected)
                    this.scheduleRecovery();
                return;
            }
            this.closeTransport();
            await this.connect();
        })();
        this.retryInflight = task;
        try {
            await task;
        }
        finally {
            this.retryInflight = null;
        }
    }
    isCurrent(generation) {
        return !this.stopped && this.generation === generation;
    }
    clearDisconnectedTimer() {
        if (this.disconnectedTimer === null)
            return;
        clearTimeout(this.disconnectedTimer);
        this.disconnectedTimer = null;
    }
    clearConnectionTimer() {
        if (this.connectionTimer === null)
            return;
        clearTimeout(this.connectionTimer);
        this.connectionTimer = null;
    }
    clearRecoveryTimer() {
        if (this.recoveryTimer === null)
            return;
        clearTimeout(this.recoveryTimer);
        this.recoveryTimer = null;
    }
    transportLost(generation) {
        if (!this.isCurrent(generation))
            return;
        this.closeTransport();
        this.setState("disconnected");
        this.options.onDisconnected?.();
        if (this.wasConnected)
            this.scheduleRecovery();
    }
    scheduleRecovery() {
        if (this.stopped || !this.wasConnected || this.recoveryTimer !== null)
            return;
        const delay = Math.min(1000 * 2 ** Math.min(this.recoveryAttempts++, 5), MAX_RECOVERY_DELAY_MS);
        this.recoveryTimer = setTimeout(() => {
            this.recoveryTimer = null;
            if (!this.stopped)
                void this.retry();
        }, delay);
    }
    // ── Refresh machinery ────────────────────────────────────────
    clearRefreshTimer() {
        if (this.refreshTimer != null) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
    /**
     * Arm `setTimeout` so `tryRefresh("preemptive")` fires roughly
     * `refreshLeadMs` (default 60s) before `streamParams.expiresAt`.
     * If `expiresAt` is missing (older backend / mocked params) or no
     * refresh callback was provided, we silently no-op — the existing
     * failed-state UI still catches things if the token does expire.
     */
    scheduleRefresh() {
        this.clearRefreshTimer();
        if (!this.options.refreshStreamParams)
            return;
        if (this.refreshUnavailable)
            return;
        const expiresAt = this.options.streamParams.expiresAt;
        if (!expiresAt || !Number.isFinite(expiresAt))
            return;
        const leadMs = this.options.refreshLeadMs ?? DEFAULT_REFRESH_LEAD_MS;
        const fireAtMs = expiresAt * 1000 - leadMs;
        // A stale or nearly expired response must never spin in a zero-delay
        // refresh loop. Retry at a measured interval until the backend catches up.
        const delayMs = Math.max(REFRESH_RETRY_MS, fireAtMs - Date.now());
        this.refreshTimer = setTimeout(() => {
            void this.tryRefresh("preemptive");
        }, delayMs);
    }
    /**
     * Single-flight refresh: invoke `refreshStreamParams()`, swap the
     * MQTT JWT in the live SignalingClient, persist the new params on
     * `this.options`, and reschedule the next pre-emptive refresh.
     *
     * Concurrent callers (e.g. timer + auth error) await the same
     * promise — `refreshInflight` clears in finally. Returns `null` on
     * any failure so callers don't have to wrap in try/catch; the
     * `onError` callback is invoked once with a typed
     * `stream_params_refresh_failed` ViewerError.
     */
    async tryRefresh(trigger) {
        if (!this.options.refreshStreamParams) {
            this.refreshUnavailable = true;
            return null;
        }
        if (this.refreshInflight)
            return this.refreshInflight;
        const refreshFn = this.options.refreshStreamParams;
        this.refreshInflight = (async () => {
            try {
                const fresh = await refreshFn();
                if (this.stopped)
                    return null;
                // Shallow-merge keeps any caller-supplied fields we didn't
                // explicitly model (forward compatibility). The fields that
                // matter for live reconnection — mqttToken / expiresAt /
                // vncPassword — are always overwritten.
                this.options.streamParams = { ...this.options.streamParams, ...fresh };
                if (this.signaling) {
                    this.signaling.refreshCredentials(this.options.streamParams.mqttToken);
                }
                this.scheduleRefresh();
                return this.options.streamParams;
            }
            catch (err) {
                if (this.stopped)
                    return null;
                const message = err instanceof Error ? err.message : String(err);
                const viewerErr = new ViewerError("stream_params_refresh_failed", `streamParams refresh failed (${trigger}): ${message}`);
                if (this._state === "connected" || trigger === "preemptive") {
                    // A transient refresh failure must not hide a working desktop.
                    // Keep trying so the JWT is ready for the next MQTT handshake.
                    this.clearRefreshTimer();
                    this.refreshTimer = setTimeout(() => { void this.tryRefresh("preemptive"); }, REFRESH_RETRY_MS);
                }
                else {
                    this.options.onError?.(viewerErr);
                }
                return null;
            }
            finally {
                this.refreshInflight = null;
            }
        })();
        return this.refreshInflight;
    }
    enqueueRemoteAnswer(sdp) {
        this.remoteAnswerQueue = this.remoteAnswerQueue.then(() => this.trySetRemoteAnswer(sdp));
    }
    async trySetRemoteAnswer(sdp) {
        if (!this.pc)
            return;
        if (this.pc.currentRemoteDescription?.type === "answer")
            return;
        if (this.pc.signalingState === "stable")
            return;
        if (this.pc.signalingState !== "have-local-offer")
            return;
        try {
            await this.pc.setRemoteDescription({ type: "answer", sdp });
        }
        catch (err) {
            this.handleError(err instanceof Error ? err : new ViewerError("set_remote_description_failed", "Failed to set remote description"));
        }
    }
    async handleRemoteIceCandidate(init) {
        if (!this.pc)
            return;
        try {
            await this.pc.addIceCandidate(init);
        }
        catch {
            // non-fatal: candidate may be invalid
        }
    }
    onDataChannelOpen(generation) {
        if (!this.dc || !this.cachedRfb)
            return;
        try {
            const dc = this.dc;
            const Rfb = this.cachedRfb;
            const rfb = new Rfb(this.options.container, dc, {
                wsProtocols: [],
            });
            this.rfb = rfb;
            rfb.scaleViewport = true;
            rfb.resizeSession = false;
            rfb.showDotCursor = true;
            // Expose for debugging (removed in prod via tree-shaking)
            globalThis.__rfb = rfb;
            rfb.addEventListener("connect", () => {
                if (!this.isCurrent(generation) || this.rfb !== rfb)
                    return;
                this.wasConnected = true;
                this.initialAuthRefreshAttempted = false;
                this.recoveryAttempts = 0;
                this.clearConnectionTimer();
                this.clearRecoveryTimer();
                this.setState("connected");
                this.options.onConnected?.();
            });
            rfb.addEventListener("disconnect", () => {
                if (this.isCurrent(generation) && this.rfb === rfb)
                    this.transportLost(generation);
            });
            rfb.addEventListener("credentialsrequired", (ev) => {
                if (!this.isCurrent(generation) || this.rfb !== rfb)
                    return;
                const { vncPassword, vncUsername } = this.options.streamParams;
                const types = ev.detail?.types ?? [];
                const needsUser = types.includes("username") || !!vncUsername;
                if (needsUser && vncUsername && vncPassword) {
                    this.rfb?.sendCredentials({ username: vncUsername, password: vncPassword });
                }
                else if (vncPassword) {
                    this.rfb?.sendCredentials({ password: vncPassword });
                }
                else {
                    this.options.onError?.(new ViewerError("vnc_password_required", "VNC password required but not provided"));
                }
            });
        }
        catch (err) {
            this.handleError(err instanceof Error ? err : new Error(String(err)));
        }
    }
    setState(state) {
        this._state = state;
    }
    handleError(error) {
        // Auth-failure recovery path: the MQTT JWT in our streamParams
        // was rejected by EMQX (CONNACK code 5/4 or MQTT5 reason 134/135).
        // Don't surface this to the user as a hard failure if a refresh
        // callback is wired up — call it, swap creds, and re-enter
        // connect() once the new JWT is in hand. Single-flight via
        // `tryRefresh` makes this safe even when timer + signaling error
        // race.
        //
        // We deliberately keep this branch quiet on `onError` (no nested
        // notification) until the refresh succeeds-or-fails; the refresh
        // path emits its own typed `stream_params_refresh_failed` error
        // when it can't recover.
        if (error instanceof SignalingError &&
            error.code === "auth_failed" &&
            this.options.refreshStreamParams &&
            !this.refreshUnavailable) {
            // An initial CONNACK rejection destroys the signaling client in
            // connect()'s catch path. Mid-session rejection can use its own
            // reconnect loop; initial rejection needs a fresh full connect.
            const needsFullConnect = this.signaling === null;
            if (!needsFullConnect || !this.initialAuthRefreshAttempted) {
                if (needsFullConnect)
                    this.initialAuthRefreshAttempted = true;
                void this.tryRefresh("reactive").then((fresh) => {
                    if (!fresh)
                        return;
                    if (needsFullConnect && !this.stopped) {
                        void this.connect();
                        return;
                    }
                    // The live signaling client's next reconnect uses the new JWT.
                });
                return;
            }
        }
        // MQTT can drop while the VNC DataChannel remains healthy. Its own
        // reconnect loop restores signaling; keep the visible desktop up.
        if (this._state === "connected")
            return;
        this.options.onError?.(error);
        if (this._state === "connecting") {
            this.setState("failed");
        }
        if (this.wasConnected && !(error instanceof ViewerError && error.code === "webrtc_unavailable")) {
            this.scheduleRecovery();
        }
    }
}
//# sourceMappingURL=viewer.js.map